import { useState } from 'react';
import { Button, Modal, Form, Spinner, Alert, Card, InputGroup } from 'react-bootstrap';
import { FiZap, FiAlertTriangle, FiChevronRight, FiChevronLeft } from 'react-icons/fi';
import './OpenAIQuestionGenerator.css';
import { FaMagic } from "react-icons/fa";

const OpenAIQuestionGenerator = ({
  onQuestionsGenerated,
  defaultTopic = '',
  defaultDifficulty = 'medium',
  defaultQuestionType = 'single',
  defaultQuestionCount = 5
}) => {
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const [showModal, setShowModal] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [includeExplanations, setIncludeExplanations] = useState(false);
  const [formData, setFormData] = useState({
    topic: defaultTopic,
    difficulty: defaultDifficulty,
    questionType: defaultQuestionType,
    questionCount: defaultQuestionCount,
    language: 'English'
  });

  const difficulties = [
    { value: 'easy', label: 'Easy', description: 'Basic recall and understanding' },
    { value: 'medium', label: 'Medium', description: 'Application of concepts' },
    { value: 'hard', label: 'Hard', description: 'Analysis and complex problems' }
  ];

  const questionTypes = [
    { value: 'single', label: 'Single Choice', icon: '1' },
    { value: 'multiple', label: 'Multiple Choice', icon: 'M' },
    { value: 'mixed', label: 'Mixed Types', icon: 'B' }
  ];

  const languages = [
    { value: 'English', label: 'English', scriptChar: 'A' },
    { value: 'Marathi', label: 'Marathi', scriptChar: 'अ' },
    { value: 'Hindi', label: 'Hindi', scriptChar: 'अ' },
    { value: 'Bengali', label: 'Bengali', scriptChar: 'অ' },
    { value: 'Telugu', label: 'Telugu', scriptChar: 'అ' },
    { value: 'Tamil', label: 'Tamil', scriptChar: 'அ' },
    { value: 'Gujarati', label: 'Gujarati', scriptChar: 'અ' },
    { value: 'Kannada', label: 'Kannada', scriptChar: 'ಅ' },
    { value: 'Malayalam', label: 'Malayalam', scriptChar: 'അ' },
    { value: 'Urdu', label: 'Urdu', scriptChar: 'ا' }
  ];

  const handleGenerate = async () => {
    setIsGenerating(true);
    setError(null);

    try {
      if (!formData.topic.trim()) {
        throw new Error('Please enter a topic for the questions');
      }

      const response = await fetch(`${API_URL}/api/generate-questions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          topic: formData.topic,
          difficulty: formData.difficulty,
          questionType: formData.questionType,
          questionCount: formData.questionCount,
          language: formData.language,
          includeExplanations: includeExplanations
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to generate questions');
      }

      const formattedQuestions = data.questions.map((question, index) => ({
        id: Date.now() + index,
        question: question.text,
        options: question.options,
        correctOption: question.type === 'single' ? question.correctAnswerIndex + 1 : null,
        correctOptions: question.type === 'multiple' ? question.correctAnswerIndices.map(i => i + 1) : [],
        type: question.type,
        explanation: question.explanation || '',
        showExplanation: !!question.explanation,
        marks: 1,
        difficulty: question.difficulty || formData.difficulty
      }));

      onQuestionsGenerated(formattedQuestions, formData.topic);
      setShowModal(false);
    } catch (err) {
      setError({
        title: 'Generation Failed',
        message: err.message,
        details: 'Please check your inputs and try again.'
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleQuestionCountChange = (value) => {
    const num = parseInt(value);
    if (!isNaN(num)) {
      setFormData(prev => ({
        ...prev,
        questionCount: Math.min(20, Math.max(1, num))
      }));
    }
  };

  const incrementCount = () => {
    if (formData.questionCount < 20) {
      setFormData(prev => ({
        ...prev,
        questionCount: prev.questionCount + 1
      }));
    }
  };

  const decrementCount = () => {
    if (formData.questionCount > 1) {
      setFormData(prev => ({
        ...prev,
        questionCount: prev.questionCount - 1
      }));
    }
  };

  const nextStep = () => {
    if (currentStep < 6) setCurrentStep(currentStep + 1);
    else handleGenerate();
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };


  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="aiqg-step-container">
            <h4 className="aiqg-step-title">What topic would you like questions about?</h4>
            <p className="aiqg-step-subtitle">Be specific for best results (e.g., "Basic Algebra" instead of "Math")</p>
            <Form.Control
              type="text"
              name="topic"
              value={formData.topic}
              onChange={handleInputChange}
              placeholder="Enter your topic here..."
              className="aiqg-topic-input"
            />
          </div>
        );
      case 2:
        return (
          <div className="aiqg-step-container">
            <h4 className="aiqg-step-title">Select difficulty level</h4>
            <p className="aiqg-step-subtitle">Choose the appropriate complexity for your questions</p>
            <div className="aiqg-option-grid">
              {difficulties.map(diff => (
                <Card
                  key={diff.value}
                  className={`aiqg-option-card ${formData.difficulty === diff.value ? 'aiqg-active' : ''}`}
                  onClick={() => handleInputChange({ target: { name: 'difficulty', value: diff.value } })}
                >
                  <Card.Body>
                    <h5 className="aiqg-option-title">{diff.label}</h5>
                    <p className="aiqg-option-desc">{diff.description}</p>
                  </Card.Body>
                </Card>
              ))}
            </div>
          </div>
        );
      case 3:
        return (
          <div className="aiqg-step-container">
            <h4 className="aiqg-step-title">Select question type</h4>
            <p className="aiqg-step-subtitle">Choose the format for your questions</p>
            <div className="aiqg-option-grid">
              {questionTypes.map(type => (
                <Card
                  key={type.value}
                  className={`aiqg-option-card ${formData.questionType === type.value ? 'aiqg-active' : ''}`}
                  onClick={() => handleInputChange({ target: { name: 'questionType', value: type.value } })}
                >
                  <Card.Body>
                    <div className="aiqg-type-icon">{type.icon}</div>
                    <h5 className="aiqg-option-title">{type.label}</h5>
                  </Card.Body>
                </Card>
              ))}
            </div>
          </div>
        );
      case 4:
        return (
          <div className="aiqg-step-container">
            <h4 className="aiqg-step-title">How many questions would you like?</h4>
            <p className="aiqg-step-subtitle">Enter a number between 1 and 20</p>

            <div className="aiqg-number-input-container">
              <InputGroup>
                <Button
                  variant="outline-secondary"
                  onClick={decrementCount}
                  disabled={formData.questionCount <= 1}
                  className="aiqg-count-btn"
                >
                  −
                </Button>
                <Form.Control
                  type="number"
                  name="questionCount"
                  value={formData.questionCount}
                  onChange={(e) => handleQuestionCountChange(e.target.value)}
                  min="1"
                  max="20"
                  className="text-center aiqg-count-input"
                />
                <Button
                  variant="outline-secondary"
                  onClick={incrementCount}
                  disabled={formData.questionCount >= 20}
                  className="aiqg-count-btn"
                >
                  +
                </Button>
              </InputGroup>
              <div className="aiqg-quick-select-buttons mt-3">
                {[5, 10, 15, 20].map(num => (
                  <Button
                    key={num}
                    variant={formData.questionCount === num ? 'primary' : 'outline-primary'}
                    onClick={() => handleQuestionCountChange(num)}
                    className="me-2 mb-2 aiqg-quick-select-btn"
                  >
                    {num}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        );
      case 5:
        return (
          <div className="aiqg-step-container">
            <h4 className="aiqg-step-title">Select question language</h4>
            <p className="aiqg-step-subtitle">Choose the language for your generated questions</p>

            <div className="aiqg-language-grid">
              {languages.map(lang => (
                <div
                  key={lang.value}
                  className={`aiqg-language-option ${formData.language === lang.value ? 'aiqg-active' : ''}`}
                  onClick={() => handleInputChange({ target: { name: 'language', value: lang.value } })}
                >
                  <div className="aiqg-language-script">
                    {lang.scriptChar}
                  </div>
                  <span className="aiqg-language-label">{lang.label}</span>
                </div>
              ))}
            </div>
          </div>
        );
      case 6:
        return (
          <div className="aiqg-step-container">
            <h4 className="aiqg-step-title">Explanation Options</h4>
            <p className="aiqg-step-subtitle">Would you like to include explanations for the answers?</p>

            <div className="aiqg-explanation-options">
              <div
                className={`aiqg-explanation-choice ${includeExplanations ? 'aiqg-active' : ''}`}
                onClick={() => setIncludeExplanations(true)}
              >
                <div className="aiqg-explanation-icon">✓</div>
                <div className="aiqg-explanation-text">
                  <h5>Yes, include explanations</h5>
                  <p>Each question will come with a detailed explanation of the correct answer</p>
                </div>
              </div>

              <div
                className={`aiqg-explanation-choice ${!includeExplanations ? 'aiqg-active' : ''}`}
                onClick={() => setIncludeExplanations(false)}
              >
                <div className="aiqg-explanation-icon">✕</div>
                <div className="aiqg-explanation-text">
                  <h5>No explanations needed</h5>
                  <p>Generate questions without answer explanations</p>
                </div>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const getProgress = () => {
    return (currentStep / 6) * 100;
  };

  return (
    <>
      <Button
        // variant="primary"
        onClick={() => {
          setShowModal(true);
          setCurrentStep(1);
          setError(null);
        }}
        className="ai-gen-btn"
      >

        <FaMagic className="ai-gen-icon" />
        <span> Generate Questions with AI</span>

      </Button>

      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        size="lg"
        centered
        className="aiqg-modal"
      >
        <Modal.Header closeButton className="aiqg-modal-header">
          <Modal.Title className="aiqg-modal-title">
            <FiZap className="text-primary me-2" />
            AI Question Generator
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="aiqg-modal-body">
          <div className="aiqg-progress-container">
            <div
              className="aiqg-progress-bar"
              style={{ width: `${getProgress()}%` }}
            ></div>
            <div className="aiqg-progress-steps">
              {[1, 2, 3, 4, 5, 6].map(step => (
                <div
                  key={step}
                  className={`aiqg-progress-step ${currentStep >= step ? 'aiqg-active' : ''}`}
                >
                  {step}
                </div>
              ))}
            </div>
          </div>

          {error && (
            <Alert variant="danger" className="aiqg-error-alert">
              <FiAlertTriangle className="me-2" />
              <div>
                <h6 className="aiqg-error-title">{error.title}</h6>
                <p className="aiqg-error-message">{error.message}</p>
                {error.details && <small className="aiqg-error-details">{error.details}</small>}
              </div>
            </Alert>
          )}

          {renderStep()}
        </Modal.Body>
        <Modal.Footer className="aiqg-modal-footer">
          <Button
            variant="outline-secondary"
            onClick={prevStep}
            disabled={currentStep === 1 || isGenerating}
            className="aiqg-prev-btn"
          >
            <FiChevronLeft className="me-1" />
            Back
          </Button>
          <Button
            variant="primary"
            onClick={nextStep}
            disabled={
              isGenerating ||
              (currentStep === 1 && !formData.topic.trim()) ||
              (currentStep === 4 && (formData.questionCount < 1 || formData.questionCount > 20))
            }
            className="aiqg-next-btn"
          >
            {currentStep < 6 ? (
              <>
                Next <FiChevronRight className="ms-1" />
              </>
            ) : isGenerating ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Generating...
              </>
            ) : (
              'Generate Questions'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default OpenAIQuestionGenerator;